/// <amd-module name="Wit/WorkItem/Service/WorkItemService" />
import * as React from "react";
import { ITfsPageService } from "Tfs/Platform/Page";
import { ILegacyComponentProperties } from "VSS/Legacy/Types";
import { Services, VssService } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssLayoutManager, IWrappedComponentProperties, WrappedComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { WorkItem, WorkItemBatchGetRequest, WorkItemErrorPolicy } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { BulkUpdateConstants, IWorkItemFieldChange } from "Wit/Common/BulkUpdateUtils/BulkUpdateInterfaces";
import { performBulkUpdate } from "Wit/Common/BulkUpdateUtils/BulkUpdateUtils";
import { CreationMode, IWorkItemFormDialogPropsWithOptionalDismiss, IWorkItemService } from "./Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { getAllowedFieldValues } from "Wit/WorkItemModel/WorkItem/FieldUtils";

class WorkItemService extends VssService implements IWorkItemService {
    private static workItemBatchSize = 200;
    private static cacheThreshold = 300000;
    private workItemsMap = new Map<number, { item: WorkItem; timestamp: number }>();

    public createWorkItem(workItemTypeName: string, creationMode?: CreationMode, options?: any, initialValues?: { [fieldName: string]: any }): void {
        if (creationMode === CreationMode.FullPagePreferred) {
            // If requested, try to navigate to the full page experience. Consuming page needs to include
            // route, otherwise this will throw an exception.
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const pageData = tfsPageService.getData();
            if (pageData && pageData.project) {
                const url = routeUrl(this.pageContext, "ms.vss-work-web.work-items-create-route", {
                    project: pageData.project.name
                });

                let creationUrl = `${url}/${workItemTypeName}`;

                if (initialValues && Object.keys(initialValues).length > 0) {
                    const params = Object.keys(initialValues).map(fieldRefName => `[${fieldRefName}]=${initialValues[fieldRefName]}`);
                    creationUrl = `${creationUrl}?${params.join("&")}`;
                }

                FastPageSwitch(this.pageContext, creationUrl, true);
            }
        } else {
            const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout(onDismiss => {
                return React.createElement(WrappedComponent, {
                    wrappedType: "legacy",
                    dependencies: ["ms.vss-web.legacy-content"],
                    wrappedProps: {
                        modules: ["WorkItemTracking/SharedScripts/WorkItemDialogShim"],
                        wrappedType: "createNewWorkItemDialog",
                        workItemTypeName,
                        onDismiss,
                        options,
                        initialValues
                    }
                } as IWrappedComponentProperties<ILegacyComponentProperties>);
            });
        }
    }

    public openWorkItemWithProps(formProps: IWorkItemFormDialogPropsWithOptionalDismiss): void {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const wrappedOnDismiss = () => {
                formProps.onDismiss?.();
                onDismiss();
            };

            return React.createElement(WrappedComponent, {
                wrappedType: "work-item-form-dialog",
                dependencies: ["ms.vss-work-web.work-item-dialogs"],
                wrappedProps: { ...formProps, onDismiss: wrappedOnDismiss }
            } as IWrappedComponentProperties<IWorkItemFormDialogPropsWithOptionalDismiss>);
        });
    }

    public openWorkItem(workItemId: number, options?: any, tryGetLatest?: boolean): void {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return React.createElement(WrappedComponent, {
                wrappedType: "legacy",
                dependencies: ["ms.vss-web.legacy-content"],
                wrappedProps: {
                    modules: ["WorkItemTracking/SharedScripts/WorkItemDialogShim"],
                    wrappedType: "WorkItemDialog",
                    workItemId,
                    onDismiss,
                    options,
                    tryGetLatest
                }
            } as IWrappedComponentProperties<ILegacyComponentProperties>);
        });
    }

    public getWorkItemUrl(workItemId: number, projectName: string): string {
        return routeUrl(this.pageContext, "ms.vss-work-web.work-item-view-route", {
            id: workItemId.toString(10),
            project: projectName
        });
    }

    public async loadWorkItem(project: string, workItemId: number, fields: string[]): Promise<WorkItem | undefined> {
        const workItems = (await Promise.all(this.loadWorkItems(project, [workItemId], fields))).flat();
        return workItems.length ? workItems[0] : undefined;
    }

    /**
     * Load work items in batches and return a list of promises
     */
    public loadWorkItems(project: string | undefined, workItemIds: number[], fields: string[]): Promise<WorkItem[]>[] {
        const groupedIds = [];
        for (var i = 0; i < workItemIds.length; i += WorkItemService.workItemBatchSize) {
            groupedIds.push(workItemIds.slice(i, i + WorkItemService.workItemBatchSize));
        }

        const client = getWorkItemTrackingClient(this.pageContext);
        return groupedIds.map(ids => {
            const requestParams = {
                errorPolicy: WorkItemErrorPolicy.Omit,
                ids,
                fields
            } as WorkItemBatchGetRequest;
            return client.getWorkItemsBatch(requestParams, project);
        });
    }

    public async setWorkItemInitialState(workItems: IWorkItem[]): Promise<IWorkItem[]> {
        const bulkUpdateTotalResult: IWorkItem[] = [];
        const stateChanges = new Map<string, number[]>();
        for (const item of workItems) {
            const initialState = this.getInitialStateForTargetType(item);
            const currentState = item.getFieldValueByName(CoreFieldRefNames.State);
            if (initialState && currentState !== initialState) {
                if (!stateChanges.has(initialState)) {
                    stateChanges.set(initialState, [item.id]);
                } else {
                    stateChanges.get(initialState)?.push(item.id);
                }
            } else {
                bulkUpdateTotalResult.push(item);
            }
        }
        for (const change of stateChanges) {
            const stateChangeList: IWorkItemFieldChange[] = [{ fieldName: CoreFieldRefNames.State, value: change[0] }];
            const stateChangesMap = new Map<string, IWorkItemFieldChange[]>();
            stateChangesMap.set(BulkUpdateConstants.AllTypes, stateChangeList);
            const bulkUpdateStateResult = await performBulkUpdate(this.pageContext, change[1], stateChangesMap);
            bulkUpdateTotalResult.push(...bulkUpdateStateResult);
        }

        return bulkUpdateTotalResult;
    }

    private getInitialStateForTargetType(workItem: IWorkItem): string | undefined {
        const stateField = workItem.getFieldByName(CoreFieldRefNames.State)!;
        const allowedStates = getAllowedFieldValues(stateField, workItem).value;

        // extract the initial state from the target work item type
        if (allowedStates.length > 0) {
            const initialState = allowedStates[0];
            return initialState;
        }
    }

    public async loadWorkItemsWithCaching(ids: number[], fields: string[], project?: string, disableCache?: boolean): Promise<WorkItem[]> {
        const workItems: WorkItem[] = [];
        const now = Date.now();
        let idsToFetch: number[] = [];

        if (disableCache) {
            idsToFetch = ids;
        } else {
            for (const id of ids) {
                const cachedItem = this.workItemsMap.get(id);
                if (cachedItem && now - cachedItem.timestamp < WorkItemService.cacheThreshold) {
                    workItems.push(cachedItem.item);
                    continue;
                }

                idsToFetch.push(id);
            }
        }

        if (idsToFetch.length) {
            const groupOfPromises = this.loadWorkItems(project, idsToFetch, fields);
            const items = (await Promise.all(groupOfPromises)).flat();
            items.forEach(item => this.workItemsMap.set(item.id, { item, timestamp: now }));

            workItems.push(...items);
        }

        return workItems;
    }

    public cleanCache() {
        this.workItemsMap.clear();
    }

    public deleteItemsFromCache(idsToDelete: number[]) {
        idsToDelete.forEach(id => {
            this.workItemsMap.delete(id);
        });
    }
}

Services.add("work-item-service", { serviceFactory: WorkItemService });
